/**
 * @file MeetingMindmapCanvas.tsx
 * @brief Interactive React Flow canvas for a meeting's mindmap.
 *
 * @details
 * The "Map" half of the Mindmap tab (04-08-2026) — the list view in
 * MeetingDetailView.tsx (`MindmapTab`) stays as the other half and remains the
 * mobile-parity view. Web gets the canvas because it has the pixels: pan, zoom,
 * minimap, and collapse/expand per branch, which is how you actually read a
 * 60-node map of an hour-long meeting.
 *
 * Layout is NOT React Flow's job — positions come from `layoutMindmap`
 * (mindmapGraph.ts), the same module that builds the tree for the list, so the
 * two views can never disagree about parentage or per-type colour. Positions
 * are recomputed whenever the expanded set changes, which does discard manual
 * node drags; that is the accepted trade (a stale hand-placed node next to a
 * freshly collapsed branch reads as a bug).
 *
 * Node heights are estimated in `mindmapNodeHeight`; the card here clamps its
 * label to the same 3 lines the estimate assumes. Change one, change both.
 *
 * The xyflow attribution badge is deliberately left in place — hiding it needs
 * a paid subscription, not a prop.
 *
 * @dependencies
 * - @xyflow/react: canvas, viewport, minimap, edge routing
 * - lucide-react: node glyphs + toolbar icons (single icon library)
 * - shared/theme/theme-context: `colorMode` follows the panel's resolved theme
 *
 * @references
 * - features/company-meetings/components/mindmapGraph.ts (tree + layout)
 * - features/company-meetings/components/MeetingDetailView.tsx (`MindmapTab`)
 * - FRONTEND/src/screens/MeetingIntelligence/MeetingDetails/tabs/MeetingMindmapTab.tsx
 *
 * @author DES
 * @date 04-08-2026
 */

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ChevronDown,
  ChevronRight,
  Maximize2,
  Minimize2,
  Scan,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/shared/theme/cn";
import { useTheme } from "@/shared/theme/theme-context";
import { isRTLText } from "@/shared/utils/formatters";
import {
  MINDMAP_NODE_WIDTH,
  buildMindmapTree,
  layoutMindmap,
  mindmapVisual,
  type Mindmap,
  type MindmapTreeNode,
} from "./mindmapGraph";

// ----------------------------------------------------------------------
// Custom node — a card, not React Flow's default box
// Accent bar + icon circle + label, matching the list rows' visual language.
// ----------------------------------------------------------------------
type MindmapCardData = {
  label: string;
  type: string | null;
  /** Children the node *has*, regardless of whether they are shown. */
  childCount: number;
  collapsed: boolean;
  /** Drives the selected ring — the node whose evidence card is open. */
  selected: boolean;
  onToggle: (id: string) => void;
};

type MindmapFlowNode = Node<MindmapCardData, "mindmapCard">;

const MindmapCard = memo(function MindmapCard({
  id,
  data,
}: NodeProps<MindmapFlowNode>) {
  const { Icon, color } = mindmapVisual(data.type);
  const isRoot = data.type === "root";
  const rtl = isRTLText(data.label);

  return (
    <div
      className={cn(
        "relative flex cursor-pointer items-start gap-2.5 overflow-hidden rounded-xl border bg-card py-2.5 pl-3 pr-2.5 shadow-sm transition-shadow",
        "hover:shadow-md",
        isRoot ? "border-transparent ring-1 ring-primary/40" : "border-border",
        data.selected && "border-brand ring-2 ring-brand/40",
      )}
      style={{ width: MINDMAP_NODE_WIDTH }}
    >
      {/* No left accent stripe (04-08-2026) — the icon circle already carries
          the type's colour, and a second coloured mark per card made a dense
          map read as stripes rather than as nodes. */}
      <Handle
        type="target"
        position={Position.Left}
        className="!h-1.5 !w-1.5 !border-0 !bg-transparent"
      />

      <span
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: `${color}1A` }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color }} />
      </span>

      <span className="min-w-0 flex-1">
        <span
          dir={rtl ? "rtl" : undefined}
          className={cn(
            "block break-words text-[13px] leading-[18px] line-clamp-3",
            isRoot ? "font-semibold" : "font-medium",
            rtl && "text-right",
          )}
        >
          {data.label}
        </span>
        {data.type && !isRoot ? (
          <span className="mt-0.5 block text-[10px] capitalize leading-[14px] text-muted-foreground">
            {data.type}
          </span>
        ) : null}
      </span>

      {data.childCount > 0 ? (
        <button
          type="button"
          // `nodrag` keeps the toggle from starting a node drag (React Flow
          // treats any pointer-down inside the node as a drag otherwise).
          className="nodrag nopan mt-0.5 flex h-6 shrink-0 items-center gap-0.5 rounded-full bg-muted px-1.5 text-[10px] font-medium text-muted-foreground hover:bg-accent"
          onClick={(e) => {
            e.stopPropagation();
            data.onToggle(id);
          }}
          aria-label={data.collapsed ? "Expand branch" : "Collapse branch"}
        >
          {data.collapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          {data.childCount}
        </button>
      ) : null}

      <Handle
        type="source"
        position={Position.Right}
        className="!h-1.5 !w-1.5 !border-0 !bg-transparent"
      />
    </div>
  );
});

// Defined once at module scope — a fresh object here re-mounts every node on
// each render, which React Flow warns about loudly.
const NODE_TYPES = { mindmapCard: MindmapCard };

// ----------------------------------------------------------------------
// Canvas
// ----------------------------------------------------------------------
type CanvasProps = {
  mindmap: Mindmap;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  /** Node whose evidence card is open, owned by the tab (the list shares it). */
  selectedId: string | null;
  onSelect: (id: string | null) => void;
};

function MindmapCanvasInner({
  mindmap,
  expanded,
  onToggle,
  onExpandAll,
  onCollapseAll,
  selectedId,
  onSelect,
}: CanvasProps) {
  const { resolvedTheme } = useTheme();
  const { fitView } = useReactFlow();
  const [tall, setTall] = useState(false);

  const tree = useMemo<MindmapTreeNode[]>(
    () => buildMindmapTree(mindmap),
    [mindmap],
  );

  const layout = useMemo(
    () => layoutMindmap(tree, expanded),
    [tree, expanded],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<MindmapFlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Layout is the source of truth for position; local drag state is disposable.
  useEffect(() => {
    setNodes(
      layout.nodes.map((n) => ({
        id: n.id,
        type: "mindmapCard" as const,
        position: { x: n.x, y: n.y },
        data: {
          label: n.node.label ?? "Untitled",
          type: n.node.type ?? null,
          childCount: n.childCount,
          collapsed: n.collapsed,
          selected: n.id === selectedId,
          onToggle,
        },
      })),
    );
    setEdges(
      layout.edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        // No `type`: React Flow's *default* edge is already the bezier we
        // want, and "bezier" is not a registered type name (v12 calls it
        // "default" / "simplebezier") — passing it logs a fallback warning
        // per edge on every render.
        style: { stroke: e.color, strokeWidth: 1.5, opacity: 0.55 },
      })),
    );
    // `selectedId` rebuilds the node list rather than patching it: positions
    // come from `layout` either way, so nothing moves, and one source of node
    // objects beats two effects racing to own `data`.
  }, [layout, selectedId, onToggle, setNodes, setEdges]);

  // Re-frame after a collapse/expand changes the map's extents. Gated on
  // `useNodesInitialized` rather than a timeout: React Flow measures nodes from
  // the DOM, and a fitView issued before measurement lands frames the *old*
  // extents and leaves half the map off-screen.
  const nodesInitialized = useNodesInitialized();
  useEffect(() => {
    if (!nodesInitialized) return;
    fitView({ padding: 0.16, duration: 260 });
  }, [nodesInitialized, layout, fitView, tall]);

  const minimapColor = useCallback(
    (node: Node) =>
      mindmapVisual((node.data as MindmapCardData)?.type ?? null).color,
    [],
  );

  return (
    <div
      // Height is inline, not a Tailwind class, on purpose: React Flow measures
      // its parent on mount and warns (and mis-frames) if it reads 0 — which it
      // does whenever the stylesheet has not applied yet.
      style={{ height: tall ? "78vh" : 520 }}
      className="relative w-full overflow-hidden rounded-xl border border-border bg-muted/20 transition-[height] duration-200"
    >
      <ReactFlow
        style={{ width: "100%", height: "100%" }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={(_, node) => onSelect(node.id)}
        // Clicking empty canvas clears the card — the same gesture that means
        // "nothing selected" everywhere else in the panel.
        onPaneClick={() => onSelect(null)}
        nodeTypes={NODE_TYPES}
        colorMode={resolvedTheme}
        fitView
        fitViewOptions={{ padding: 0.16 }}
        minZoom={0.2}
        maxZoom={1.6}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: false }}
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
        <Controls showInteractive={false} position="bottom-left" />
        {/* Opposite corner from the zoom controls, and only once the map is big
            enough to get lost in — on a 10-node map it is just an occluder. */}
        {nodes.length > 14 ? (
          <MiniMap
            pannable
            zoomable
            position="bottom-right"
            nodeColor={minimapColor}
            nodeStrokeWidth={0}
            className="!h-20 !w-32 !rounded-lg !border !border-border !bg-card/80"
          />
        ) : null}

        <Panel position="top-left" className="!m-2 flex flex-wrap gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 bg-card px-2.5 text-xs"
            onClick={onExpandAll}
          >
            <ChevronDown className="h-3.5 w-3.5" />
            Expand all
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 bg-card px-2.5 text-xs"
            onClick={onCollapseAll}
          >
            <ChevronRight className="h-3.5 w-3.5" />
            Collapse all
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 bg-card px-2.5 text-xs"
            onClick={() => fitView({ padding: 0.16, duration: 260 })}
          >
            <Scan className="h-3.5 w-3.5" />
            Fit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 bg-card px-2.5 text-xs"
            onClick={() => setTall((v) => !v)}
          >
            {tall ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
            {tall ? "Shrink" : "Enlarge"}
          </Button>
        </Panel>
      </ReactFlow>
    </div>
  );
}

/**
 * React Flow hooks (`useReactFlow`) need a provider above them, and the
 * provider must not be the same component that calls them — hence the split.
 */
export function MeetingMindmapCanvas(props: CanvasProps) {
  return (
    <ReactFlowProvider>
      <MindmapCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
